import React, { useEffect, useState } from 'react'
import { Panel } from '@components/Panel/Panel'
import { StakingBadge } from '@components/StakingBadge/StakingBadge'
import { contractAddressFromSpaceId, isSpaceStreamId } from '@towns-protocol/sdk'
import { Box, FancyButton, Icon, Paragraph, PopupMenu, Stack, Text } from '@ui'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { StakableOperator, useFetchOperatorsQuery } from 'hooks/useOperatorData'
import { WalletReady } from 'privy/WalletReady'
import { shimmerClass } from 'ui/styles/globals/shimmer.css'
import {
    BlockchainTransactionType,
    NULL_ADDRESS,
    useIsTransactionPending,
    useSpaceDelegationOperator,
    useSpaceId,
} from 'use-towns-client'

import { NodeOperatorIcon } from '../NodeOperatorIcon/NodeOperatorIcon'

import { useDelegateToNodeOperator } from './hooks/useDelegateToNodeOperator'
import { NodeOperatorCard, NodeOperatorSkeletonCard } from './operators/NodeOperatorCard'

export const DelegateToNodeOperatorPanel = () => {
    const { enableDelegation } = useFeatureFlags()
    if (enableDelegation) {
        return <DelegateToNodeOperatorPanelEnabled />
    }

    // fallback
    return <DelegateToNodeOperatorPanelDisabled />
}

const DelegateToNodeOperatorPanelEnabled = () => {
    const [selectedOperatorAddress, setSelectedOperatorAddress] = useState<
        `0x${string}` | undefined
    >()
    const spaceId = useSpaceId()
    const spaceAddress =
        spaceId && isSpaceStreamId(spaceId)
            ? (contractAddressFromSpaceId(spaceId) as `0x${string}`)
            : undefined

    // ------------------------------------------------------------
    // grab operators and data

    const { data: operatorsData } = useFetchOperatorsQuery()
    const operators = operatorsData?.operators

    // ------------------------------------------------------------
    // if space is currently delegated, select the operator

    const { data: currentDelegation } = useSpaceDelegationOperator(spaceAddress)

    const isDelegated = currentDelegation && currentDelegation !== NULL_ADDRESS
    const defaultOperatorAddress = operators?.[0]?.address

    useEffect(() => {
        if (isDelegated) {
            setSelectedOperatorAddress(currentDelegation)
        } else if (defaultOperatorAddress) {
            setSelectedOperatorAddress(defaultOperatorAddress)
        }
    }, [currentDelegation, isDelegated, defaultOperatorAddress])

    // ------------------------------------------------------------
    // delegate to node callback

    const { delegateToNode } = useDelegateToNodeOperator({
        operatorMetadata: operators?.find(
            (o) => o.address.toLowerCase() === selectedOperatorAddress?.toLowerCase(),
        ),
        operatorAddress: selectedOperatorAddress,
        spaceAddress,
    })

    const isTransactionPending = useIsTransactionPending(
        BlockchainTransactionType.AddSpaceDelegation,
    )

    // ------------------------------------------------------------
    // render

    const label = isDelegated ? 'Edit Node Operator Delegation' : 'Delegate to Node Operator'
    const isLoaded = Boolean(operators?.length)

    const selectedOperator = operators?.find(
        (o) => o.address.toLowerCase() === selectedOperatorAddress?.toLowerCase(),
    )

    return (
        <Panel label={label}>
            {!isDelegated && <StakeBanner />}
            {isLoaded && selectedOperatorAddress && spaceAddress ? (
                <Stack gap="md">
                    <Paragraph strong color="default">
                        Select Node Operator:
                    </Paragraph>
                    <Stack gap="sm">
                        <NodeOperatorMenu
                            nodeOperators={operators || []}
                            selectedOperator={selectedOperatorAddress}
                            onChange={setSelectedOperatorAddress}
                        />
                        {selectedOperator ? (
                            <NodeOperatorCard
                                operator={selectedOperator}
                                operatorAddress={selectedOperator.address}
                            />
                        ) : (
                            <NodeOperatorSkeletonCard />
                        )}
                    </Stack>
                    <WalletReady>
                        {({ getSigner }) => (
                            <FancyButton
                                cta
                                compact
                                disabled={isTransactionPending}
                                borderRadius="lg"
                                type="submit"
                                onClick={() => delegateToNode(getSigner)}
                            >
                                {isDelegated ? 'Update' : 'Delegate to Operator'}
                            </FancyButton>
                        )}
                    </WalletReady>
                </Stack>
            ) : (
                <Stack
                    centerContent
                    padding
                    horizontal
                    gap
                    className={shimmerClass}
                    borderRadius="md"
                    minHeight="x8"
                >
                    <Paragraph>Loading operators...</Paragraph>
                </Stack>
            )}
        </Panel>
    )
}

const DelegateToNodeOperatorPanelDisabled = () => (
    <Panel centerContent label="Delegate to Node Operator">
        <Icon type="nokey" />
    </Panel>
)

export const NodeOperatorMenu = ({
    nodeOperators,
    selectedOperator,
    onChange,
}: {
    nodeOperators: StakableOperator[]
    selectedOperator: `0x${string}`

    onChange: (operator: `0x${string}`) => void
}) => (
    <PopupMenu
        options={nodeOperators}
        padding="sm"
        renderButton={(option) => (
            <Stack horizontal padding="xs" gap="md">
                <Box grow justifyContent="center" alignItems="start">
                    <Text>{option.name}</Text>
                </Box>
                <Box centerContent>
                    <Icon type="down" />
                </Box>
            </Stack>
        )}
        renderOption={(option) => (
            <Stack horizontal gap="md">
                <Box>
                    <NodeOperatorIcon nodeOperator={option} size="sm" />
                </Box>
                <Box justifyContent="center">
                    <Paragraph>{option.name}</Paragraph>
                </Box>
            </Stack>
        )}
        value={nodeOperators.find((o) => o.address === selectedOperator) ?? nodeOperators[0]}
        getKey={(option) => option.address}
        onChange={(option) => onChange(option.address)}
    />
)

const StakeBanner = () => (
    <Stack horizontal padding="md" background="level2" gap="md" rounded="md">
        <Box centerContent>
            <StakingBadge showIcon type="diamond" />
        </Box>
        <Box gap="paragraph">
            <Paragraph strong size="lg" color="default">
                Unlock yield and exclusive badges
            </Paragraph>
            <Paragraph size="sm" color="gray2">
                Delegate to an operator before the token launch to make sure members who stake to
                your town will receive badges and earn yield!
            </Paragraph>
        </Box>
    </Stack>
)
